import { useEffect, useRef, useState } from 'react';
import {
  api,
  can,
  type Attestation,
  type ControlSummary,
  type DriftReport,
  type Finding,
  type Framework,
  type MeView,
  type ProductSummary,
  type RiskAcceptance,
  type ProgressReport,
  type Scan,
  type Tenant,
} from './api.ts';
import { entitledTo } from './billing.ts';
import { billingApi, type BillingProduct, type BillingSummary, type TenantPlan } from './billingApi.ts';
import { Assessments } from './tenant/Assessments.tsx';
import { AcceptedRisks } from './tenant/AcceptedRisks.tsx';
import { AttestationRow } from './tenant/AttestationRow.tsx';
import { FindingsPanel } from './tenant/FindingsPanel.tsx';
import { FrameworksCard } from './tenant/FrameworksCard.tsx';
import { Overview } from './tenant/Overview.tsx';
import { PlanPanel } from './tenant/PlanPanel.tsx';
import { TenantScores } from './platform/TenantScores.tsx';
import { mayLoadScores } from './platform/scores.ts';
import { Progress } from './tenant/Progress.tsx';
import { ScheduleCard } from './tenant/ScheduleCard.tsx';
import { ServiceReview } from './tenant/ServiceReview.tsx';
import { Thresholds } from './tenant/Thresholds.tsx';
import { message } from './tenant/format.ts';
import { IDLE_LABEL, isInFlight, stageLabel } from './tenant/scanStatus.ts';
import type { ScheduleView } from './tenant/types.ts';
import './tenant.css';

interface Props {
  me: MeView;
  tenant: Tenant;
  /**
   * Supplied by the shell, which owns the breadcrumb and the page title for
   * this drill-down. Declared so the shell may keep passing it; this screen
   * deliberately renders no header of its own.
   */
  onBack?: () => void;
}

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'findings', label: 'Findings' },
  /**
   * Between Findings and Assessments on purpose. Findings is this client's
   * position today, Progress is how it got there, and Assessments is the run
   * log that produced both — the reader's questions in that order.
   */
  { id: 'progress', label: 'Progress' },
  /**
   * Straight after Progress: the same material addressed to the client rather
   * than to us. Not a control on the Progress tab — the period there is ours
   * and unnamed, the period here is one the operator names for a document that
   * leaves the console, and a date picker above that table would read as a
   * filter on it.
   */
  { id: 'review', label: 'Service review' },
  { id: 'assessments', label: 'Assessments' },
  { id: 'schedule', label: 'Schedule' },
  /**
   * Next to Schedule rather than under Settings, because the two are the same
   * kind of decision about the same client: how often we assess them, and what
   * their report is addressed to.
   */
  { id: 'coverage', label: 'Coverage' },
  /**
   * Straight after Coverage, because the plan is what Coverage may choose
   * from: a framework is bought here and projected there, and the Coverage
   * card links across when one is missing.
   */
  { id: 'plan', label: 'Plan' },
  { id: 'attestations', label: 'Attestations' },
  /**
   * Beside Attestations, because both record something the client said rather
   * than something we read: one answers a question no API can, the other
   * answers a failure the client has decided to live with.
   */
  { id: 'accepted', label: 'Accepted risks' },
  { id: 'thresholds', label: 'Thresholds' },
] as const;

type TabId = (typeof TABS)[number]['id'];

/**
 * How often the console re-reads the scan list while an assessment is under
 * way. A scan takes roughly three quarters of a minute, so three seconds shows
 * each stage promptly without asking the API fifteen questions to do it — and
 * the poll reads the scan list alone, never the five-call `refresh()`.
 */
const POLL_INTERVAL_MS = 3_000;

/**
 * ...and how long it keeps asking.
 *
 * A worker killed mid-collection leaves its row on `collecting` for ever, and a
 * button disabled off that row would be dead for ever with it. Five minutes is
 * several times the longest healthy scan, so nothing that is actually working
 * reaches the cap; past it the operator is told plainly and given the button
 * back. A second read of a tenant costs an API quota. A client nobody can ever
 * assess again costs the product.
 */
const POLL_CAP_MS = 5 * 60 * 1_000;

const STALLED_MESSAGE =
  'This assessment has not reported back in five minutes, so DriftWatch has stopped waiting on it. ' +
  'It may still be finishing — reload to check — or you can run another.';

/**
 * What to say about a run that ended without producing an assessment.
 *
 * Two endings, not one. A failure is something about this client's tenant that
 * needs looking at; an interruption is a DriftWatch worker that was restarted
 * mid-run and implies nothing at all about the client. Telling the operator the
 * first when it was the second sends them hunting for a collection error that
 * was never raised.
 */
const unfinishedMessage = (scan: Scan): string => {
  const lead =
    scan.status === 'interrupted' ? 'The assessment was interrupted' : 'The assessment failed';
  return scan.error === null
    ? `${lead}, and no reason was recorded against it.`
    : `${lead}: ${scan.error}`;
};

/**
 * Repeating a sentence verbatim in a live region says nothing: the text node is
 * unchanged, so nothing is spoken. Alternating an invisible zero-width space
 * makes two identical announcements two different strings. Same device as the
 * Sweep screen.
 */
const spoken = (previous: string, next: string): string =>
  previous.endsWith('\u200B') ? next : `${next}\u200B`;

/**
 * Client detail: the latest assessment, its findings, the run history, the
 * cadence, the attestations and the thresholds.
 *
 * This file is the shell only — it loads the data every panel reads and hands
 * each one its props. The panels themselves live in `./tenant/`, one to a file,
 * and none of them fetches anything.
 *
 * The attestation panel is what makes AC-005 and AC-013 answerable at all. Two
 * of the fifteen controls turn on organisational facts that no API exposes, and
 * rather than guess at them the product asks — and stamps the resulting finding
 * as resting on someone's word.
 *
 * The tabs are local state on purpose. There is no router in this console and
 * six panels over one client do not justify introducing one; the shell owns the
 * breadcrumb that got the reader here.
 */
export const TenantDetail = ({ me, tenant }: Props) => {
  const [tab, setTab] = useState<TabId>('overview');
  const [scans, setScans] = useState<Scan[]>([]);
  const [attestations, setAttestations] = useState<Attestation[]>([]);
  const [keys, setKeys] = useState<string[]>([]);
  const [controls, setControls] = useState<ControlSummary[]>([]);
  const [products, setProducts] = useState<ProductSummary[]>([]);
  const [acceptances, setAcceptances] = useState<RiskAcceptance[]>([]);
  const [chosen, setChosen] = useState<readonly string[] | null>(tenant.reportFrameworks);
  const [overrides, setOverrides] = useState<Record<string, Record<string, unknown>>>({});
  const [schedule, setSchedule] = useState<ScheduleView | null>(null);
  /**
   * The catalogue's frameworks. Read once per client rather than on every
   * refresh: it is deployment-wide data that changes when we ship, not when
   * this client does anything.
   */
  const [frameworkCatalogue, setFrameworkCatalogue] = useState<Framework[]>([]);
  /**
   * Which of the catalogue's frameworks this account's package covers, or null
   * for no restriction.
   *
   * Read here beside the catalogue because both are facts about the deployment
   * and the account rather than about this client, and because the coverage
   * card needs them together: offering a framework the package excludes gets a
   * 409 from `PUT /tenants/:id/frameworks` after somebody has ticked it.
   *
   * `undefined` is "not answered yet" and is distinct from `null`, which is a
   * package with no framework restriction. The card derives its initial state
   * from this, and `useState` initialisers do not run twice — so it must not be
   * mounted until the answer is in, or a restricted account would get a card
   * built from the whole catalogue.
   */
  const [allowedFrameworks, setAllowedFrameworks] = useState<readonly string[] | null | undefined>(
    undefined,
  );
  /**
   * The price list and the account's billing summary, which carries this
   * client's plan and the entitlement the server derived from it.
   *
   * Three states for the same reason as `allowedFrameworks`: `undefined` is
   * not answered yet, and the coverage card must not be built before it is.
   * `null` is a read that failed — a server from before billing, say — and
   * means no plan restriction, never "nothing allowed": the server refuses
   * anything outside the plan either way.
   */
  const [billing, setBilling] = useState<
    { products: BillingProduct[]; summary: BillingSummary } | null | undefined
  >(undefined);
  const [billingError, setBillingError] = useState<string | null>(null);
  const [findings, setFindings] = useState<Finding[] | null>(null);
  const [drift, setDrift] = useState<DriftReport | null>(null);
  const [driftDetail, setDriftDetail] = useState<string | null>(null);
  const [progress, setProgress] = useState<ProgressReport | null>(null);
  /**
   * Kept apart from `error`. A history that could not be read is a fact about
   * one tab, and routing it to the screen-level banner would put a red message
   * above an Overview that is perfectly correct.
   */
  const [progressError, setProgressError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');
  /**
   * The click has been accepted but no row has appeared for it yet. This is the
   * *only* thing the local flag covers: the button's disabled state is otherwise
   * derived from the scan list, so a reload, a second tab, or a scan the
   * scheduler started all disable it without anybody having clicked.
   */
  const [starting, setStarting] = useState(false);
  /**
   * The scan the poll gave up on (see `POLL_CAP_MS`), so it stops disabling the
   * button.
   *
   * An id rather than a flag, because the row does not go away when we stop
   * watching it: a wedged `collecting` row stays newest for ever, and a plain
   * flag cleared on the next click would re-disable the button on that same
   * wedged row for another five minutes. Naming the scan we abandoned means a
   * genuinely new one still disables it.
   */
  const [abandonedScanId, setAbandonedScanId] = useState<string | null>(null);
  /**
   * The newest scan id at the moment the button was pressed, or null if this
   * client had none.
   *
   * `POST /tenants/:id/scans` answers 202 once the job is on the queue; the row
   * is written by the worker that picks it up. So for a second or two after the
   * click the newest scan is still the *previous* one, and a poll that did not
   * know that would read a run finishing before it had begun. Held in a ref
   * because it steers the poll without redrawing anything — and because putting
   * it in state would restart the poll, and with it the five-minute cap.
   */
  const startedFrom = useRef<{ readonly previousScanId: string | null } | null>(null);

  const say = (text: string) => setAnnouncement((previous) => spoken(previous, text));

  const refresh = () => {
    void Promise.all([
      api.listScans(tenant.id),
      api.listAttestations(tenant.id),
      api.listControls(),
      api.listRiskAcceptances(tenant.id),
      api.listOverrides(tenant.id),
      // The plan covers every tenant and reading it queues nothing, so the
      // detail screen takes this tenant's row out of it rather than the server
      // growing a second endpoint that answers the same question.
      api.getSchedule(),
    ])
      .then(([scanResponse, attestationResponse, controlResponse, acceptanceResponse, overrideResponse, plan]) => {
        setScans(scanResponse.scans);
        setAttestations(attestationResponse.attestations);
        setKeys(attestationResponse.keys);
        setControls(controlResponse.controls);
        setProducts(controlResponse.products);
        setAcceptances(acceptanceResponse.acceptances);
        setOverrides(overrideResponse.overrides);

        const decision = [...plan.enqueue, ...plan.deferred, ...plan.skipped].find(
          (row) => row.tenantId === tenant.id,
        );
        setSchedule(
          decision ? { defaultCadenceDays: plan.defaultCadenceDays, decision } : null,
        );
      })
      .catch((cause: unknown) => setError(message(cause)));
  };

  // Back to what the record says whenever the screen is pointed at another
  // client. Today it is always remounted through the list, so this changes
  // nothing; the day it is not, a stale selection would silently mislabel
  // somebody else's findings.
  useEffect(() => setChosen(tenant.reportFrameworks), [tenant.id, tenant.reportFrameworks]);

  useEffect(() => {
    void api
      .listFrameworks()
      .then((response) => setFrameworkCatalogue(response.frameworks))
      .catch((cause: unknown) => setError(message(cause)));
    /*
     * A failure here leaves the restriction unknown, and unknown has to mean
     * "no restriction": narrowing the card on a call that did not answer would
     * hide frameworks the account has paid for. The server refuses anything
     * outside the package either way, so the cost of being wrong this way is a
     * 409 rather than a coverage section somebody cannot get back.
     */
    void api
      .accountPackage()
      .then((response) => setAllowedFrameworks(response.allowance.frameworks))
      .catch(() => setAllowedFrameworks(null));
    void Promise.all([billingApi.billingCatalogue(), billingApi.billingSummary()])
      .then(([catalogue, summary]) => setBilling({ products: catalogue.products, summary }))
      .catch((cause: unknown) => {
        setBilling(null);
        setBillingError(message(cause));
      });
  }, []);

  const plan: TenantPlan | null =
    billing?.summary.tenants.find((row) => row.tenantId === tenant.id) ?? null;
  /** Null is a client whose plan does not restrict it, including any whose plan could not be read. */
  const entitledFrameworks = plan?.entitlement.frameworks ?? null;

  /*
   * The saved plan replaces this client's row, and the account total follows
   * it, so the Coverage card re-derives from what the server just said rather
   * than from a second read.
   */
  const planSaved = (saved: TenantPlan) =>
    setBilling((current) => {
      if (current === null || current === undefined) return current;
      const others = current.summary.tenants.filter((row) => row.tenantId !== saved.tenantId);
      const tenants = [...others, saved];
      return {
        ...current,
        summary: {
          ...current.summary,
          tenants,
          totalPence: tenants.reduce((sum, row) => sum + row.totalPence, 0),
        },
      };
    });

  useEffect(() => {
    // A different client: drop whatever the previous one's run left behind, or
    // its stage would be read off this one's button.
    startedFrom.current = null;
    setStarting(false);
    setAbandonedScanId(null);
    setError(null);
    setAnnouncement('');
    refresh();
  }, [tenant.id]);

  /**
   * The frameworks this client's reports are actually projected against.
   *
   * The same two narrowings the pipeline makes at render time, in the same
   * order: what has been chosen for this client, then what their account's
   * package covers. A screen that grouped by a framework their report does not
   * contain would be showing a projection we do not print.
   *
   * An unanswered package read (`undefined`) narrows nothing, matching the
   * coverage card: it re-derives on the next render when the answer lands, and
   * nothing here holds it in state to go stale.
   */
  /*
   * Held here rather than read from the `tenant` prop on every render.
   *
   * The prop comes from the client list and is not re-read while this screen
   * is open, so saving the coverage left every finding showing the criteria of
   * the previous selection until somebody reloaded the page — the labels and
   * the coverage sections disagreeing about the same client. The card reports
   * what it saved and this follows it.
   */
  const chosenFrameworks = chosen;
  const reportFrameworks = frameworkCatalogue.filter(
    (framework) =>
      (allowedFrameworks === undefined ||
        allowedFrameworks === null ||
        allowedFrameworks.includes(framework.id)) &&
      entitledTo(entitledFrameworks, framework.id) &&
      (chosenFrameworks === null || chosenFrameworks.includes(framework.id)),
  );

  // The scan list arrives newest first, so the first complete row is the
  // assessment every headline on this screen is about.
  const latest = scans.find((scan) => scan.status === 'complete') ?? null;
  const latestId = latest?.id ?? null;

  /**
   * Whether this client has an assessment under way, decided from the data
   * rather than from anything this screen remembers doing — which is what makes
   * the button correct on mount, after a reload, in a second tab, and when the
   * scheduler was the one that started it.
   *
   * `starting` covers only the gap before this run's row exists, and
   * `abandonedScanId` is the escape hatch for a row that never moves.
   */
  const newest = scans[0] ?? null;
  const activeScan =
    newest !== null && isInFlight(newest.status) && newest.id !== abandonedScanId ? newest : null;
  const running = activeScan !== null || starting;
  const runLabel = running ? stageLabel(activeScan?.status ?? null) : IDLE_LABEL;

  /**
   * The findings of that assessment, and what moved since the one before it.
   * Both are read-only routes keyed on the scan id, so this reloads only when a
   * newer assessment completes rather than on every refresh of the panels
   * above.
   */
  useEffect(() => {
    if (latestId === null) {
      setFindings(null);
      setDrift(null);
      setDriftDetail(null);
      return;
    }

    let live = true;
    void Promise.all([api.scanFindings(tenant.id, latestId), api.scanDrift(tenant.id, latestId)])
      .then(([findingResponse, driftResponse]) => {
        if (!live) return;
        setFindings(findingResponse.findings);
        setDrift(driftResponse.drift);
        setDriftDetail(driftResponse.detail ?? null);
      })
      .catch((cause: unknown) => {
        if (live) setError(message(cause));
      });

    return () => {
      live = false;
    };
  }, [tenant.id, latestId]);

  /**
   * The history behind this client, re-read when a newer assessment lands.
   *
   * Its own effect rather than a fourth call in the one above, because it is the
   * only read on this screen that is not keyed on a scan: it spans the window,
   * and it is answerable — with `insufficientHistory` set — for a client that
   * has no completed assessment at all. Folding it into an effect that returns
   * early on `latestId === null` would leave that client looking at a spinner
   * for ever.
   */
  useEffect(() => {
    let live = true;
    setProgressError(null);
    void api
      .progress(tenant.id)
      .then((response) => {
        if (live) setProgress(response.progress);
      })
      .catch((cause: unknown) => {
        if (!live) return;
        setProgress(null);
        setProgressError(message(cause));
      });

    return () => {
      live = false;
    };
  }, [tenant.id, latestId]);

  /**
   * Watch a run to its end.
   *
   * The scan itself takes about forty-five seconds; the HTTP call that starts it
   * takes milliseconds, which is why nothing here is driven off that promise.
   * Only the cheap list is read on each tick — the five-call `refresh()` waits
   * until the run has actually landed, because that is the only moment findings,
   * verdict counts and drift have moved.
   */
  useEffect(() => {
    if (!running) return;

    let live = true;
    // Measured from the browser's clock at the moment the watch began. The
    // scan's own `startedAt` is the server's, and the two need not agree.
    const deadline = Date.now() + POLL_CAP_MS;

    const giveUp = (wedged: Scan | null) => {
      startedFrom.current = null;
      setStarting(false);
      // Null means no row ever appeared, so there is nothing to abandon and
      // clearing `starting` alone gives the button back.
      if (wedged !== null) setAbandonedScanId(wedged.id);
      setError(STALLED_MESSAGE);
      say(STALLED_MESSAGE);
    };

    const tick = () => {
      void api
        .listScans(tenant.id)
        .then((response) => {
          if (!live) return;
          setScans(response.scans);

          const top = response.scans[0] ?? null;
          const marker = startedFrom.current;
          if (marker !== null) {
            // The worker has not written this run's row yet, so the newest row
            // is still the one that was newest when the button was pressed.
            if (top === null || top.id === marker.previousScanId) {
              // Nothing of ours to abandon: the row we would blame is the
              // previous run's, and it is not what we are waiting on.
              if (Date.now() >= deadline) giveUp(null);
              return;
            }
            startedFrom.current = null;
            setStarting(false);
          }

          if (top === null || isInFlight(top.status)) {
            if (Date.now() >= deadline) giveUp(top);
            return;
          }

          // It has left the in-flight set, so everything below the button has
          // changed and the expensive read is finally worth doing.
          //
          // Tested for success rather than for failure, which is the direction
          // that stays correct as the server grows terminal statuses. The
          // inverse shipped as `=== 'failed'`, and `interrupted` — a run that
          // produced nothing — would have been announced as "Assessment
          // complete" by the else branch, with no report to open behind it.
          if (top.status === 'complete') {
            say('Assessment complete.');
          } else {
            setError(unfinishedMessage(top));
            say(
              top.status === 'interrupted'
                ? 'The assessment was interrupted.'
                : 'The assessment failed.',
            );
          }
          refresh();
        })
        .catch((cause: unknown) => {
          if (live) setError(message(cause));
        });
    };

    const timer = setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      live = false;
      clearInterval(timer);
    };
  }, [tenant.id, running]);

  /**
   * The stage, spoken. The button's label is the only thing on this screen that
   * moves for forty-five seconds, and a reader who has tabbed away from it hears
   * nothing at all otherwise. The terminal announcements are made by the poll,
   * on the tick that sees the run end, and this deliberately does not overwrite
   * them: by then `running` is false.
   */
  useEffect(() => {
    if (running) say(runLabel);
  }, [running, runLabel]);

  const runScan = async () => {
    setError(null);
    // Set before the request, so there is no frame in which the button looks
    // clickable again. Nothing clears it in a `finally`: the 202 means queued,
    // not finished, and clearing it there is the bug this screen had.
    startedFrom.current = { previousScanId: scans[0]?.id ?? null };
    setStarting(true);
    try {
      await api.startScan(tenant.id);
    } catch (cause) {
      // Includes the server's own refusal — 409 when it can still see a scan
      // running (ADR-0035). Its sentence is the one the operator reads; this
      // screen does not restate the rule.
      startedFrom.current = null;
      setStarting(false);
      setError(message(cause));
    }
  };

  const openReport = (scanId: string, print: boolean) => {
    void api
      .openReport(tenant.id, scanId, { print })
      .catch((cause: unknown) => setError(message(cause)));
  };

  return (
    <>
      <div className="tabs-underline td-tabs">
        {TABS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            className="tab"
            aria-selected={tab === entry.id}
            aria-controls="td-panel"
            role="tab"
            onClick={() => setTab(entry.id)}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {/* One region for the screen, in the same shape every other screen in the
          console uses. The run button's label changes four times during a scan
          and none of those changes is spoken without it. */}
      <p aria-live="polite" role="status" className="sr-only">
        {announcement}
      </p>

      {error !== null && (
        <div className="error td-error" role="status">
          {error}
        </div>
      )}

      <div id="td-panel" className="td-panel">
        {tab === 'overview' && (
          <Overview
            tenant={tenant}
            latest={latest}
            findings={findings}
            drift={drift}
            driftDetail={driftDetail}
            schedule={schedule}
            running={running}
            runLabel={runLabel}
            canRun={can(me, 'scans.run')}
            onRun={() => void runScan()}
            onOpenReport={openReport}
          />
        )}

        {/* Staff only (ADR-0081). Gated here rather than inside the panel so
            that a customer's Overview never mounts it, and so never asks. */}
        {tab === 'overview' && mayLoadScores(me) && <TenantScores tenantId={tenant.id} />}

        {tab === 'findings' && (
          <FindingsPanel
            latest={latest}
            findings={findings}
            controls={controls}
            products={products}
            frameworks={reportFrameworks}
            acceptances={acceptances}
          />
        )}

        {tab === 'progress' && (
          <Progress progress={progress} error={progressError} frameworks={reportFrameworks} />
        )}

        {tab === 'review' && (
          <ServiceReview tenantId={tenant.id} scans={scans} onError={setError} />
        )}

        {tab === 'assessments' && (
          <Assessments scans={scans} onOpenReport={openReport} />
        )}

        {tab === 'schedule' &&
          (schedule === null ? (
            <p className="note">
              This client is not in the current sweep plan, so there is no cadence to show yet.
            </p>
          ) : (
            <ScheduleCard
              tenantId={tenant.id}
              view={schedule}
              onSaved={refresh}
              onError={setError}
            />
          ))}

        {/*
          Both reads have to have landed before the card is built: it derives
          its ticks from the catalogue and narrows them by the package, and
          neither is re-derived after mount.
        */}
        {tab === 'coverage' &&
          (frameworkCatalogue.length === 0 ||
          allowedFrameworks === undefined ||
          billing === undefined ? (
            <p className="note">Loading framework coverage…</p>
          ) : (
            <FrameworksCard
              // Rebuilt when the plan changes: the card derives its ticks once,
              // and a plan saved on the next tab would otherwise leave it
              // offering what it offered before.
              key={`${tenant.id}:${entitledFrameworks?.join(',') ?? '*'}`}
              tenantId={tenant.id}
              catalogue={frameworkCatalogue}
              allowed={allowedFrameworks}
              entitled={entitledFrameworks}
              onOpenPlan={() => setTab('plan')}
              selected={chosen}
              onSaved={(frameworks) => {
                setChosen(frameworks);
                refresh();
              }}
              onError={setError}
            />
          ))}

        {tab === 'plan' &&
          (billing === undefined ? (
            <p className="note">Loading this client’s plan…</p>
          ) : billing === null ? (
            <p className="note">
              This client’s plan could not be read{billingError === null ? '' : `: ${billingError}`}
            </p>
          ) : (
            <PlanPanel
              key={tenant.id}
              tenantId={tenant.id}
              products={billing.products}
              plan={plan}
              catalogue={frameworkCatalogue}
              canManage={can(me, 'billing.manage')}
              hasSubscription={billing.summary.hasSubscription}
              billingEnabled={billing.summary.billingEnabled}
              onSaved={planSaved}
            />
          ))}

        {tab === 'attestations' && (
          <section className="td-stack">
            <p className="lead td-lead">
              Two controls depend on facts no API exposes. Until they are answered here those
              controls report as open questions — never as met.
            </p>
            {keys.map((key) => (
              <AttestationRow
                key={key}
                attestationKey={key}
                existing={attestations.find((item) => item.key === key)}
                tenantId={tenant.id}
                onSaved={refresh}
                onError={setError}
              />
            ))}
          </section>
        )}

        {tab === 'accepted' && (
          <AcceptedRisks
            tenantId={tenant.id}
            acceptances={acceptances}
            findings={findings}
            controls={controls}
            canAccept={can(me, 'risks.accept')}
            onChanged={refresh}
            onError={setError}
          />
        )}

        {tab === 'thresholds' && <Thresholds controls={controls} overrides={overrides} />}
      </div>
    </>
  );
};
